import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Portal } from 'react-portal'
import * as styles from './tasksList.module.scss'
import Svg from '../../svg/svg'
import { getTaskTypeId, getTaskTypeLabel } from '../../../views/tasks/helpers'
import { SnackBarState } from '../../snackBar/types'
import {
   useActiveLocations,
   useCategories,
   useConditions,
   useConnections,
   useItemGetKits,
   useItemHistory,
   useItemNotes,
   useItems,
   useItemsView,
   useItemTypes,
   useItemUpload,
   useManufacturers,
   useNotifications,
   useProfile,
} from '../../../../hooks'
import { NotificationTypes, Severity, ToolTypes } from '../../../../types'
import SnackBar, { closedSnackbarState } from '../../snackBar/snackBar'
import {
   Alert,
   Button,
   CrudItems,
   LoadingOverlay,
   Modal,
   NoResults,
   WarningModal,
} from '../..'
import classNames from 'classnames'
import Task from './task/task'
import { CrudItemsParentView } from '../../../../constants/crudItems'
import { Actions } from '../../../../constants/tables'
import { ItemDetails } from './task/types'
import { NoteModal } from '../../../views/notes/noteModal'
import { TabStatus } from '../../crudItems/constants'
import { ItemButton } from '../../modal/types'
import {
   InventoryWarehouse,
   ItemWarehouse,
} from '../../crudItems/inventory/types'
import { useMediaQuery } from 'react-responsive'
import { ACTION_CONSTANTS } from '../../../views/actions/constants'
import { CheckoutAction } from '../../itemAuditedList/itemAuditedList.type'
import {
   convertInventoryWarehouseToOption,
   convertItemWarehouseToInventoryWarehouse,
   convertOptionToInventoryWarehouse,
} from '../../crudItems/inventory/helpers'
import { CRUD_TABLE } from '../../../views/items/constants'
import {
   convertCategoriesToOptions,
   convertConditionsToOptions,
   convertConnectionsToOptions,
   convertItemTypesToOptions,
   convertLocationToOptions,
   convertManufacturersToOptions,
   convertNoteTypesToOptions,
} from '../../filters/helpers'
import { Option } from '../../form/fields/dropdown/types'
import {
   AddToKitModal,
   ManageKitModal,
   RemoveFromKitModal,
} from '../../kitModals'
import ItemQuantityModal from '../../../views/items/itemQuantityModal/itemQuantityModal'
import { CheckoutModal } from '../../../actionsWorkflow'
import AdjustModal from '../../adjustModal/adjustModal'
import { TasksListProps } from './types'

const TasksList = ({
   selectedTaskType,
   onTaskUpdate,
   totalTasks,
   isEmployeeUser,
   isViewOnlyUser,
   enableLocationTracking,
}: TasksListProps) => {
   const listRef = useRef<HTMLUListElement>(null)
   const [currentPage, setCurrentPage] = useState<number>(1)
   const [snackbar, setSnackbar] = useState<SnackBarState>(closedSnackbarState)
   const [isWarningModalOpen, setIsWarningModalOpen] = useState<boolean>(false)

   const notificationType = useMemo(
      () => getTaskTypeId(selectedTaskType),
      [selectedTaskType]
   )

   const {
      acceptReject,
      deleteGlobal,
      dismiss,
      isLastPage,
      isLoading: isTasksLoading,
      isLoadingMore: isPaging,
      list,
      isDismissSuccess,
      isAcceptRejectSuccess,
      dismissAllInfo,
      isDismissAllInfoSuccess,
      refetch: refreshCurrentPage,
   } = useNotifications({
      onChange: onTaskUpdate,
      totalItems: totalTasks,
      notificationType,
      currentPage: currentPage,
   })

   useEffect(() => {
      setCurrentPage(1)
   }, [notificationType])

   useEffect(() => {
      if (isDismissSuccess) {
         setSnackbar({
            message: 'The task was dismissed',
            severity: Severity.SUCCESS,
         })
      }
   }, [isDismissSuccess])

   useEffect(() => {
      if (isDismissAllInfoSuccess) {
         setSnackbar({
            message: 'All info tasks were dismissed',
            severity: Severity.SUCCESS,
         })
      }
   }, [isDismissAllInfoSuccess])

   useEffect(() => {
      if (isAcceptRejectSuccess) {
         setSnackbar({
            message: 'The action was updated.',
            severity: Severity.SUCCESS,
         })
      }
   }, [isAcceptRejectSuccess])

   // Start of Note Modal Functionality
   const [isNoteModalVisible, setIsNoteModalVisible] = useState<boolean>(false)
   const [selectedNoteId, setSelectedNoteId] = useState<number | null>(null)
   const [selectedItemNotificationId, setSelectedItemNotificationId] = useState<
      number | null
   >(null)
   const [selectedItem, setSelectedItem] = useState<any>(null)

   const {
      data: itemNotesData,
      itemNotesList: getItemNotes,
      refetch: refetchNotes,
      isLoading: isLoadingNotes,
      isRefetching: isRefetchingNotes,
   } = useItemNotes()

   const handleViewNote = useCallback((itemData: ItemDetails) => {
      const { NoteId, ToolLoanId, ToolId, Barcode, CustomText1Value, Title } =
         itemData
      const stringToolId = String(ToolId)
      if (!NoteId) {
         return setSnackbar({
            message: 'There is no note associated with this task.',
            severity: Severity.ERROR,
         })
      }
      setSelectedItem({
         ID: stringToolId,
         Title: Title,
         Barcode: Barcode,
         SerialNumber: '',
         CustomText1Value: CustomText1Value,
         ToolLoanId: ToolLoanId,
      })
      setSelectedNoteId(NoteId)
      setIsNoteModalVisible(true)
   }, [])

   const handleNoteModalSave = useCallback(() => {
      // clear selected ids on note modal close
      setSelectedNoteId(null)
      setIsNoteModalVisible(false)
      setSnackbar({
         message: 'The Note was updated',
         severity: Severity.SUCCESS,
      })
      refreshCurrentPage()
      onTaskUpdate()
   }, [refreshCurrentPage, onTaskUpdate])
   // End of Note Modal Functionality

   // Start of Item Modal Functionality
   const { profile } = useProfile()
   const [isItemModalVisible, setIsItemModalVisible] = useState<boolean>(false)
   const [activeValue, setActiveValue] = useState<any>({})
   const [itemForm, setItemForm] = useState<any>({})
   const isMobile = useMediaQuery({ query: '(max-width: 1000px)' })
   const [itemImages, setItemImages] = useState<any>([])
   const [tabStatus, setTabStatus] = useState<TabStatus>(TabStatus.DETAILS)
   const [itemId, setItemId] = useState<number | null>(null)
   const [itemAction, setItemAction] = useState<Actions>(Actions.Readonly)
   const [isCopyPrompt, setIsCopyPrompt] = useState<boolean>(false)
   const [isDeletePrompt, setIsDeletePrompt] = useState<boolean>(false)
   const [isNewItemNote, setIsNewItemNote] = useState<boolean>(false)
   const [isManageKitModalOpen, setIsManageKitModalOpen] =
      useState<boolean>(false)
   const [isAddToKitModalOpen, setIsAddToKitModalOpen] =
      useState<boolean>(false)
   const [isRemoveFromKitModal, setIsRemoveFromKitModal] =
      useState<boolean>(false)
   const [addToKitQty, setAddToKitQty] = useState<number>(1)
   const [itemKitOptions, setItemKitOptions] = useState([])
   const [isItemLimitPrompt, setIsItemLimitPrompt] = useState<boolean>(false)
   const [selectedItemsForWorkflow, setSelectedItemsForWorkflow] = useState<
      any[]
   >([])

   const checkoutOptions = useMemo(
      () => ACTION_CONSTANTS.companyCheckoutOptions,
      [ACTION_CONSTANTS]
   )
   const [checkoutAction, setCheckoutAction] = useState<CheckoutAction>(
      checkoutOptions[0]
   )
   const [isAdjustModalOpen, setIsAdjustModalOpen] = useState<boolean>(false)
   const [isCheckoutModalOpen, setIsCheckoutModalOpen] =
      useState<boolean>(false)
   const [isQuantityModalOpen, setIsQuantityModalOpen] =
      useState<boolean>(false)

   const [selectedWarehouseForCheckout, setSelectedWarehouseForCheckout] =
      useState<InventoryWarehouse | null>(null)
   const [selectedTransferToWarehouse, setSelectedTransferToWarehouse] =
      useState<InventoryWarehouse | null>(null)
   const [warehouseSelectionDisabled, setWarehouseSelectionDisabled] =
      useState<boolean>(null)

   const inventoryWarehouses: InventoryWarehouse[] = useMemo(
      () =>
         itemForm?.Warehouses?.map((wh) =>
            convertItemWarehouseToInventoryWarehouse(wh)
         ) ?? [],
      [itemForm]
   )

   const warehouseOptions = useMemo(
      () =>
         inventoryWarehouses
            ?.filter((wh) => wh?.toolWarehouseId)
            .map((wh) => convertInventoryWarehouseToOption(wh)) ?? [],
      [inventoryWarehouses]
   )

   const resetRef = useRef<HTMLInputElement | null>(null)

   const itemInventoryWarehouse = useMemo(
      () => convertItemWarehouseToInventoryWarehouse(itemForm.Warehouse),
      [itemForm?.Warehouse]
   )

   useEffect(() => {
      if (selectedWarehouseForCheckout) {
         setAddToKitQty(selectedWarehouseForCheckout.quantity)
      } else if (itemInventoryWarehouse) {
         setAddToKitQty(itemInventoryWarehouse.quantity)
      } else {
         setAddToKitQty(1)
      }
   }, [selectedWarehouseForCheckout, itemInventoryWarehouse])

   const allItemButtons: ItemButton[] = [
      {
         name: 'actionLoan',
         title: 'Loan',
         action: () => handleWorkflowAction('Loan', itemInventoryWarehouse),
         type: 'Loan',
      },
      {
         name: 'actionTransfer',
         title: 'Transfer',
         action: () => handleWorkflowAction('Transfer', itemInventoryWarehouse),
         type: 'TransferTo',
      },
      {
         name: 'actionReturn',
         title: 'Return',
         action: () => handleWorkflowAction('Return', itemInventoryWarehouse),
         type: 'CheckIn',
      },
      {
         name: 'copy',
         title: 'Copy',
         action: () =>
            profile.AllowAddTool
               ? handleCopyPrompt()
               : setIsItemLimitPrompt(true),
         type: '',
      },
      {
         name: 'adjust',
         title: 'Adjust',
         action: () => handleAdjustClick(itemInventoryWarehouse),
         type: 'Adjust',
      },
      {
         name: 'remove',
         title: 'Delete',
         action: () => handleDeletePrompt(),
         type: 'DeleteTool',
      },
      {
         name: 'add-note',
         title: 'Note',
         action: () => handleAddNote(),
         type: 'AddNote',
      },
      {
         name: 'manage-kit',
         title: 'Manage Kit',
         action: () => manageKit(),
         type: 'AddtokitOpenItems',
      },
      {
         name: 'add-to-kit',
         title: 'Add to Kit',
         action: () => addToKit(itemInventoryWarehouse),
         type: 'AddtokitOpenKit',
      },
      {
         name: 'remove-from-kit',
         title: 'Remove from Kit',
         action: () => removeFromKit(),
         type: 'RemoveFromKit',
      },
      {
         name: 'makeUnavailable',
         title: 'Make Unavailable',
         action: () => handleWorkflowAction('Make Unavailable'),
         type: 'MakeUnavailable',
      },
      {
         name: 'makeAvailable',
         title: 'Make Available',
         action: () => handleWorkflowAction('Make Available'),
         type: 'MakeAvailable',
      },
   ]

   const { list: connections } = useConnections()
   const { list: manufacturers } = useManufacturers()
   const { list: categories } = useCategories()
   const { list: locations } = useActiveLocations()
   const { list: conditions } = useConditions()
   const { list: itemTypes } = useItemTypes()

   const itemTypeOptions = useMemo(
      () => convertItemTypesToOptions(itemTypes),
      [itemTypes]
   )

   const connectionOptions = useMemo(
      () => convertConnectionsToOptions(connections),
      [connections]
   )

   const manufacturerOptions = useMemo(
      () => convertManufacturersToOptions(manufacturers),
      [manufacturers]
   )

   const categoryOptions = useMemo(
      () => convertCategoriesToOptions(categories),
      [categories]
   )

   const locationOptions = useMemo(
      () => convertLocationToOptions(locations),
      [locations]
   )

   const conditionOptions = useMemo(
      () => convertConditionsToOptions(conditions),
      [conditions]
   )

   const {
      upload,
      changePosition,
      deleteImage,
      isPositionLoading,
      isPositionSuccess,
   } = useItemUpload()

   const {
      data: itemHistoryData,
      getItemHistoryData,
      isLoading: isItemHistoryLoading,
   } = useItemHistory()

   const {
      data: itemData,
      getItemView,
      isLoading: isItemLoading,
      isRefetching: isItemRefetching,
      isSuccess: isItemSuccess,
   } = useItemsView()

   const {
      setPath,
      error,
      remove,
      add,
      isRemoving,
      isSuccessRemove,
      isErrorRemove,
      isAdding,
      isSuccessAdd,
      isErrorAdd,
   } = useItems()

   const {
      result: getItemKitData,
      addKit,
      getKits,
      deleteKit,
      isSuccessKits,
      isErrorCreate: isErrorAddToKit,
      isSuccessCreate: isSuccessAddToKit,
      isErrorDeleteKit,
      isSuccessDeleteKit,
      isLoadingKits,
      isCreating: isAddingToKit,
      isDeletingKit,
   } = useItemGetKits()

   useEffect(() => {
      setPath({
         ...CRUD_TABLE.crudPaths, 
         skipPost: true
      }, CRUD_TABLE.key)
   }, [])

   useEffect(() => {
      // if itemform isnt empty then set selected item
      if (Object.keys(itemForm).length !== 0) {
         setSelectedItemsForWorkflow([itemForm])
      }
   }, [itemForm])

   useEffect(() => {
      //handles setting the item modal form
      if (itemData?.data?.Data && isItemSuccess) {
         let data = itemData?.data?.Data
         showActionForm(
            itemAction ?? Actions.Edit,
            data?.Id,
            data?.Title,
            data?.ManufacturerId,
            data?.Category,
            data
         )
      }
   }, [itemData, isItemSuccess])

   useEffect(() => {
      if (isSuccessKits && getItemKitData?.data) {
         setItemKitOptions(convertNoteTypesToOptions(getItemKitData.data))
         setIsAddToKitModalOpen(true)
      }
   }, [isSuccessKits, getItemKitData])

   useEffect(() => {
      if (isSuccessRemove) {
         handleSnackbar('Item has been deleted.', Severity.SUCCESS)
         closeModalAndRefresh()
      }
   }, [isSuccessRemove])

   useEffect(() => {
      if (isErrorRemove && error) {
         handleSnackbar(error, Severity.ERROR)
         closeModalAndRefresh()
      }
   }, [isErrorRemove])

   useEffect(() => {
      if (isSuccessAdd) {
         const copiedItemName = `${itemForm.Title} (Copy)`
         handleSnackbar(
            `Item was copied with the name "${copiedItemName}". Please visit the items page to edit it.`,
            Severity.SUCCESS
         )
         closeModalAndRefresh()
      }
   }, [isSuccessAdd])

   useEffect(() => {
      if (isErrorAdd && error) {
         handleSnackbar(error, Severity.ERROR)
      }
   }, [isErrorAdd])

   useEffect(() => {
      if (isSuccessAddToKit) {
         handleSnackbar('Item has been added to the kit.', Severity.SUCCESS)
         closeModalAndRefresh()
      }
   }, [isSuccessAddToKit])

   useEffect(() => {
      if (isErrorAddToKit && error) {
         handleSnackbar(error, Severity.ERROR)
         closeModalAndRefresh()
      }
   }, [isErrorAddToKit])

   useEffect(() => {
      if (isSuccessDeleteKit) {
         handleSnackbar('Item has been removed from the kit.', Severity.SUCCESS)
         closeModalAndRefresh()
      }
   }, [isSuccessDeleteKit])

   useEffect(() => {
      if (isErrorDeleteKit && error) {
         handleSnackbar(error, Severity.ERROR)
         closeModalAndRefresh()
      }
   }, [isErrorDeleteKit])

   const handleSnackbar = (message: string, severity: Severity) => {
      setSnackbar((prevState) => ({ ...prevState, message, severity }))
   }

   const handleDeletePrompt = () => {
      setIsDeletePrompt(true)
      handleItemModalClose()
   }

   const handleDelete = (id: number) => {
      setItemId(id)
      handleActionForm(Actions.Delete)
      setIsDeletePrompt(false)
   }

   const deleteItem = () => remove({ id: itemId })

   const handleCopyPrompt = () => {
      setIsCopyPrompt(true)
      handleItemModalClose()
   }

   const handleCopy = () => {
      handleActionForm(Actions.Copy)
      setIsCopyPrompt(false)
   }

   const copyItem = () => {
      const newItem = {
         ...itemForm,
         Title: `${itemForm.Title} (Copy)`,
         ManufacturerId: itemForm.ManufacturerId,
         copyId: itemForm.Id,
         SerialNumber: '',
         Barcode: '',
         Id: null,
         ToolLoanId: 0,
         localUniqueId: null,
         LocationTrackingLabel: null,
         CustomRentalRate1: itemForm.CustomRentalRate1Value,
         CustomRentalRate2: itemForm.CustomRentalRate2Value,
         CustomRentalRate3: itemForm.CustomRentalRate3Value,
         CustomRentalRate4: itemForm.CustomRentalRate4Value,
         itemImages: itemImages
            ?.filter((image) => !image.IsMatchedImage)
            ?.map((image) => ({
               OriginalId: image.OriginalId,
               Position: image.Position,
            })),
         Warehouses:
            itemForm?.Warehouses?.length > 0
               ? itemForm.Warehouses.map((warehouse: ItemWarehouse) => ({
                    WarehouseLocationId: warehouse.WarehouseLocationId,
                    totalQuantity: warehouse.AvailableQuantity,
                    reorderLevelQuantity: warehouse.ReorderLevelQuantity,
                    IsHomeWarehouse: warehouse.IsHomeWarehouse,
                 }))
               : [],
      }
      add(newItem)
      setItemAction(Actions.Readonly)
   }

   const handleAddNote = () => {
      setIsNewItemNote(true)
      setTabStatus(TabStatus.NOTES)
   }

   const manageKit = () => {
      setIsManageKitModalOpen(true)
      setIsItemModalVisible(false)
   }

   const addToKit = (
      warehouse?: InventoryWarehouse,
      disableWarehouseSelection?: boolean
   ) => {
      setSelectedWarehouseForCheckout(warehouse)
      getKits({
         defaultLocationID: warehouse?.id ?? itemForm.DefaultLocationID,
         status: itemForm.Status,
         assignedToUserID: itemForm.AssignedToUserID,
         requestedLocationId: itemForm.RequestedLocationId,
         locationID: warehouse?.id ?? itemForm.LocationID,
         skip: null,
         take: null,
         query: null,
      })
      setWarehouseSelectionDisabled(disableWarehouseSelection ?? false)
   }

   const handleAddToKit = (obj: any) => {
      handleItemModalClose()
      addKit(obj)
   }

   const removeFromKit = () => {
      handleItemModalClose()
      setIsRemoveFromKitModal(true)
   }

   const handleRemoveFromKit = (obj: any) => {
      deleteKit(obj)
      setIsRemoveFromKitModal(false)
   }

   const handleAdjustClick = (
      selectedWarehouse: InventoryWarehouse,
      disableWarehouseSelection?: boolean
   ) => {
      setWarehouseSelectionDisabled(disableWarehouseSelection ?? false)
      setSelectedWarehouseForCheckout(selectedWarehouse)
      setIsAdjustModalOpen(true)
   }

   const postItem = (item: any, localUniqueId) => {
      const formData = new FormData()
      formData.append('toolId', localUniqueId)
      formData.append('position', item.Position)
      formData.append('file[0]', item['File[0]'])
      formData.append('file[1]', item['File[1]'])
      upload(formData)
   }

   const showActionForm = (
      action: number | ((prevState: number) => number),
      id?: number,
      title?: string,
      manufacturerId?: number,
      category?: string,
      itemListData = []
   ) => {
      const obj = itemListData
      setItemId(id)
      setItemAction(action)

      setActiveValue(obj)
      setItemForm(obj)
      setItemImages([])

      getItemNotes(String(id))
      getItemHistoryData(String(id))
      setIsItemModalVisible(true)
   }

   const handleActionForm = (action: number, id?: number, obj?: any) => {
      switch (action) {
         case Actions.Copy:
            copyItem()
            break
         case Actions.Delete:
            deleteItem()
            break
         default:
            break
      }
   }

   const refetchNoteTableData = () => refetchNotes()

   const isQuantityItemsSelected = (array: any[]) =>
      array.some(
         (x) =>
            x.ToolType == ToolTypes['Consumable'] ||
            x.ToolType == ToolTypes['Quantity Item']
      )

   const handleIncrementDecrementQuantity = (item: any, val: string) => {
      const itemToAdjust = selectedItemsForWorkflow.find(
         (y) => y.localUniqueId === item.localUniqueId
      )
      if (val === 'dec' && itemToAdjust.quantityForCart > 1) {
         itemToAdjust.quantityForCart = itemToAdjust.quantityForCart - 1
      } else if (
         val === 'inc' &&
         itemToAdjust.quantityForCart < itemToAdjust.StatusQuantity
      ) {
         itemToAdjust.quantityForCart = itemToAdjust.quantityForCart + 1
      } else {
         return
      }
      setSelectedItemsForWorkflow([...selectedItemsForWorkflow])
   }

   const handleCustomTotalChange = (e: any, item: any) => {
      e.target.value = parseInt(e.target.value)
      const itemToAdjust = selectedItemsForWorkflow.find(
         (y) => y.localUniqueId === item.localUniqueId
      )
      const adjustedValue =
         e.target.value === '' ? e.target.value : Number(e.target.value)
      // @ts-ignore
      itemToAdjust.quantityForCart = adjustedValue
      setSelectedItemsForWorkflow([...selectedItemsForWorkflow])
   }

   const handleWarehouseChange = (selectedWarehouse: Option | null) => {
      if (isAddToKitModalOpen) {
         getKits({
            defaultLocationID: Number(selectedWarehouse?.value),
            status: itemForm.Status,
            assignedToUserID: itemForm.AssignedToUserID,
            requestedLocationId: itemForm.RequestedLocationId,
            locationID: Number(selectedWarehouse?.value),
            skip: null,
            take: null,
            query: null,
         })
      }
      const checkoutWarehouse = selectedWarehouse
         ? inventoryWarehouses?.find(
              (x) => x.id === Number(selectedWarehouse.value)
           )
         : null
      setSelectedWarehouseForCheckout(checkoutWarehouse)
      const updatedItems = selectedItemsForWorkflow.map((x) => ({
         ...x,
         quantityForCart: 1, // set initial quantity to 1
         toolWarehouseId: checkoutWarehouse
            ? checkoutWarehouse.toolWarehouseId
            : null,
      }))
      setSelectedItemsForWorkflow(updatedItems)
   }

   const handleTransferToWarehouseChange = (
      selectedWarehouse: Option | null
   ) => {
      const transferToInvWarehouse = selectedWarehouse
         ? convertOptionToInventoryWarehouse(selectedWarehouse)
         : null
      setSelectedTransferToWarehouse(transferToInvWarehouse)
      const updatedItems = selectedItemsForWorkflow.map((x) => ({
         ...x,
         NewLocation: selectedWarehouse?.label,
      }))
      setSelectedItemsForWorkflow(updatedItems)
   }

   const handleWorkflowModalsClose = useCallback(() => {
      setIsCheckoutModalOpen(false)
      setIsQuantityModalOpen(false)
      setSelectedTransferToWarehouse(null)
   }, [])

   useEffect(() => {
      if (!isCheckoutModalOpen) {
         setSelectedWarehouseForCheckout(null)
         setSelectedTransferToWarehouse(null)
      }
   }, [isCheckoutModalOpen])

   const handleCommenceWorkflow = () => {
      if (isQuantityModalOpen) {
         setIsQuantityModalOpen(false)
      }
      setIsCheckoutModalOpen(true)
   }

   const handleWorkflowAction = (
      workflowOption: string,
      warehouse?: InventoryWarehouse,
      disableSelection?: boolean
   ) => {
      let workflowPath
      if (workflowOption === 'Transfer') {
         if (selectedItemsForWorkflow[0].StatusDesc === 'Available') {
            workflowPath = checkoutOptions.find(
               (x) => x.label === 'Warehouse Transfer'
            )
         } else {
            workflowPath = checkoutOptions.find(
               (x) => x.label === 'Field Transfer'
            )
         }
      } else {
         workflowPath = checkoutOptions.find((x) => x.label === workflowOption)
      }
      setCheckoutAction(workflowPath)

      if (
         isQuantityItemsSelected(selectedItemsForWorkflow) &&
         workflowOption !== 'Make Available' &&
         workflowOption !== 'Make Unavailable'
      ) {
         let quantityForCart: number
         if (workflowOption === 'Warehouse Transfer') {
            quantityForCart = warehouse?.quantity ?? 1
         } else if (workflowOption?.includes('Transfer')) {
            quantityForCart = selectedItemsForWorkflow[0]?.StatusQuantity ?? 1
         } else {
            quantityForCart = 1
         }
         const quantItem = {
            ...selectedItemsForWorkflow[0],
            quantityForCart: quantityForCart,
         }
         setSelectedWarehouseForCheckout(warehouse ?? null)
         setSelectedItemsForWorkflow([quantItem])
         setIsQuantityModalOpen(true)
         setWarehouseSelectionDisabled(disableSelection ?? false)
      } else {
         handleCommenceWorkflow()
      }
   }

   const handleItemModalClose = () => {
      setIsItemModalVisible(false)
      setIsNewItemNote(false)
      setSelectedNoteId(null)
      setTabStatus(TabStatus.DETAILS)
      setItemImages([])
      setActiveValue({})
      setIsAddToKitModalOpen(false)
      setIsAdjustModalOpen(false)
      setIsRemoveFromKitModal(false)
      resetRef?.current?.click()
   }

   const closeModalAndRefresh = useCallback(() => {
      handleItemModalClose()
      onTaskUpdate()
      refreshCurrentPage()
   }, [handleItemModalClose, onTaskUpdate, refreshCurrentPage])

   const handleViewItem = useCallback((itemData: ItemDetails) => {
      const { ToolLoanId, ToolId, ToolWarehouseId, KitId, NotificationId } =
         itemData

      const stringToolId = String(ToolId)
      if (!ToolId) {
         return setSnackbar({
            message: 'There is no item associated with this task.',
            severity: Severity.ERROR,
         })
      }
      setSelectedNoteId(null)
      setSelectedItemNotificationId(NotificationId)
      getItemNotes(stringToolId)
      getItemHistoryData(stringToolId)
      getItemView({
         Id: ToolId,
         ToolLoanId: ToolLoanId,
         toolWarehouseId: ToolWarehouseId,
         KitId: KitId,
      })
   }, [])
   // End of Item Modal Functionality

   const handleDismissAllClick = useCallback(
      () => setIsWarningModalOpen(true),
      []
   )

   const handleConfirmDismissAll = useCallback(() => {
      setIsWarningModalOpen(false)
      dismissAllInfo()
   }, [])

   const handleCancelDismissAll = useCallback(
      () => setIsWarningModalOpen(false),
      []
   )

   const isGlobal = useMemo(
      () => notificationType === NotificationTypes.Global,
      [notificationType]
   )

   const handleDismissComplete = useCallback(
      (id: number) => {
         isGlobal ? deleteGlobal(id) : dismiss(id)
      },
      [isGlobal, deleteGlobal, dismiss]
   )

   const handleAcceptDecline = useCallback(
      (id: number, accept: boolean) => {
         acceptReject(id, accept)
      },
      [acceptReject]
   )

   const scrollToTop = useCallback(() => {
      if (listRef.current) {
         listRef.current.scrollTop = 0
      }
   }, [])

   const handlePreviousPage = useCallback(() => {
      scrollToTop()
      setCurrentPage(currentPage - 1)
   }, [currentPage, scrollToTop])

   const handleNextPage = useCallback(() => {
      scrollToTop()
      setCurrentPage(currentPage + 1)
   }, [currentPage, scrollToTop])

   const title = useMemo(
      () => getTaskTypeLabel(selectedTaskType),
      [selectedTaskType]
   )

   const titleIconId = useMemo(() => {
      switch (title) {
         case 'Actions':
            return 'action-tasks'
         case 'Reminders':
            return 'reminders'
         case 'Info':
            return 'info-tasks'
         case 'Global':
            return 'global-tasks'
         default:
            return 'tasks'
      }
   }, [title])

   const svgStyle = useMemo(
      () => ({
         top: titleIconId === 'action-tasks' ? '3px' : 'auto',
         width: titleIconId === 'global-tasks' ? '30px' : '22px',
         height: titleIconId === 'global-tasks' ? '30px' : '22px',
      }),
      [titleIconId]
   )

   const noResultsMessage = useMemo(() => {
      switch (notificationType) {
         case NotificationTypes.Action:
            return 'No Actions to Take'
         case NotificationTypes.Reminder:
            return 'No Open Reminders'
         default:
            return 'All Caught Up!'
      }
   }, [notificationType])

   const contentClassName = classNames(styles.content, {
      [styles.removeScroll]: isTasksLoading || isPaging,
   })

   const showList = useMemo(
      () => list?.length > 0 && !isTasksLoading,
      [list, isTasksLoading]
   )

   const showPaging = useMemo(
      () => totalTasks > 10 && !isTasksLoading,
      [totalTasks, isTasksLoading]
   )

   const showSpinner = useMemo(
      () =>
         isItemLoading ||
         isItemRefetching ||
         isRemoving ||
         isAdding ||
         isAddingToKit ||
         isDeletingKit ||
         isLoadingKits ||
         isTasksLoading ||
         isPaging ||
         isLoadingNotes ||
         isRefetchingNotes ||
         isItemLoading ||
         isItemHistoryLoading ||
         isPositionLoading,
      [
         isItemLoading,
         isItemRefetching,
         isRemoving,
         isAdding,
         isAddingToKit,
         isDeletingKit,
         isLoadingKits,
         isTasksLoading,
         isPaging,
         isLoadingNotes,
         isRefetchingNotes,
         isItemLoading,
         isItemHistoryLoading,
         isPositionLoading,
      ]
   )

   return (
      <section className={styles.container}>
         <header className={styles.header}>
            <div className={styles.flexContainer}>
               <h2>
                  <Svg id={titleIconId} stroke="black" style={svgStyle} />
                  <span>{title}</span>
               </h2>
               {title === 'Info' && list?.length > 0 && (
                  <button
                     type="button"
                     className={styles.dismissButton}
                     onClick={handleDismissAllClick}
                  >
                     Dismiss All
                  </button>
               )}
            </div>
         </header>
         <div className={contentClassName}>
            {showSpinner && (
               <LoadingOverlay />
            )}

            {list?.length === 0 && !showSpinner && (
               <NoResults>
                  <p>{noResultsMessage}</p>
               </NoResults>
            )}

            {showList && (
               <ul className={styles.list} ref={listRef}>
                  {list.map((item) => (
                     <li key={item.Id}>
                        <Task
                           taskType={notificationType}
                           taskDetails={item}
                           onAcceptDeclineClick={handleAcceptDecline}
                           onDismissClick={handleDismissComplete}
                           onCompleteClick={handleDismissComplete}
                           onViewItemClick={handleViewItem}
                           onViewNoteClick={handleViewNote}
                        />
                     </li>
                  ))}
               </ul>
            )}

            {showPaging && (
               <div className={styles.pagingButtons}>
                  <Button
                     onClick={handlePreviousPage}
                     disabled={currentPage === 1}
                     variant="text"
                     minWidth="max-content"
                     small
                  >
                     Previous Page
                  </Button>
                  <Button
                     onClick={handleNextPage}
                     disabled={isLastPage}
                     variant="text"
                     minWidth="max-content"
                     small
                  >
                     Next Page
                  </Button>
               </div>
            )}
         </div>
         {isNoteModalVisible && (
            <Portal>
               <NoteModal
                  noteId={selectedNoteId}
                  itemDetails={selectedItem}
                  isModalOpen={isNoteModalVisible}
                  setIsModalOpen={setIsNoteModalVisible}
                  onSaveNote={handleNoteModalSave}
                  isReadOnly={false}
               />
            </Portal>
         )}
         {isItemModalVisible && (
            <Modal
               isModalVisible={isItemModalVisible}
               closeModal={handleItemModalClose}
               title={itemForm?.Title ?? ''}
               itemButtons={allItemButtons}
               permissions={itemForm.Permissions ?? []}
               marginTop={isMobile && '2rem'}
               isItemModal
            >
               <CrudItems
                  isNewItemNote={isNewItemNote}
                  setIsNewItemNote={setIsNewItemNote}
                  postItem={postItem}
                  setItemImages={setItemImages}
                  itemImages={itemImages}
                  deleteImage={deleteImage}
                  changePosition={changePosition}
                  isPositionSuccess={isPositionSuccess}
                  setTabStatus={setTabStatus}
                  tabStatus={tabStatus}
                  activeValue={activeValue}
                  isLoading={showSpinner}
                  itemTypes={itemTypeOptions}
                  categories={categoryOptions}
                  manufacturers={manufacturerOptions}
                  locations={locationOptions}
                  conditions={conditionOptions}
                  itemNotesData={itemNotesData}
                  itemHistoryData={itemHistoryData}
                  handleAction={handleActionForm}
                  closeModal={handleItemModalClose}
                  itemId={itemId}
                  itemAction={itemAction}
                  isViewOnlyUser={isViewOnlyUser}
                  isModalVisible={isItemModalVisible}
                  itemForm={itemForm}
                  setItemForm={setItemForm}
                  resetRef={resetRef}
                  getItemView={getItemView}
                  refetchNoteTableData={refetchNoteTableData}
                  parentPageView={CrudItemsParentView.Task_Item}
                  parentTaskListDetails={{ notificationType, currentPage, totalItems: totalTasks }}
                  handleWorkflowAction={handleWorkflowAction}
                  handleAdjustClick={handleAdjustClick}
                  handleAddToKitClick={addToKit}
                  isAdminCompanyUser={!isEmployeeUser}
               />
            </Modal>
         )}
         {isCopyPrompt && (
            <WarningModal
               isModalOpen={isCopyPrompt}
               handleClose={() => setIsCopyPrompt(false)}
               title={'Copy Confirmation'}
               message={'Are you sure you want to copy this item?'}
               primaryButtonTitle={'Ok'}
               primaryButtonMethod={handleCopy}
               primaryButtonVariant={'primary'}
               secondaryButtonTitle={'Cancel'}
               secondaryButtonMethod={() => setIsCopyPrompt(false)}
               secondaryButtonVariant={'tertiary'}
            />
         )}
         {isItemLimitPrompt && (
            <WarningModal
               isModalOpen={isItemLimitPrompt}
               handleClose={() => setIsItemLimitPrompt(false)}
               title={'Item Limit Reached'}
               hasChildren
               message="Need more items?"
               zIndex="9999"
               primaryButtonTitle={'Close'}
               primaryButtonVariant={'tertiary'}
               primaryButtonMethod={() => setIsItemLimitPrompt(false)}
            >
               <Alert message="You have reached the item limit for your free account. Explore ShareMyToolbox paid system for more items and capabilities." />
            </WarningModal>
         )}
         {isDeletePrompt && (
            <WarningModal
               isModalOpen={isDeletePrompt}
               handleClose={() => setIsDeletePrompt(false)}
               title={'Delete Item'}
               message={'Are you sure you want to delete this item?'}
               primaryButtonTitle={'Delete'}
               primaryButtonMethod={(id) => handleDelete(id)}
               primaryButtonVariant={'danger'}
               secondaryButtonTitle={'Cancel'}
               secondaryButtonMethod={() => setIsDeletePrompt(false)}
               secondaryButtonVariant={'tertiary'}
               hasChildren={itemForm?.Warehouses?.length > 1}
            >
               <Alert
                  severity={Severity.WARNING}
                  message="All available quantities of this item will be
                     removed from all warehouses."
               />
            </WarningModal>
         )}
         {isManageKitModalOpen && (
            <ManageKitModal
               isModalOpen={isManageKitModalOpen}
               handleClose={() => setIsManageKitModalOpen(false)}
               kitId={itemId}
               itemForm={itemForm}
            />
         )}
         {isAddToKitModalOpen && (
            <AddToKitModal
               isModalOpen={isAddToKitModalOpen}
               handleClose={() => handleItemModalClose()}
               handleAddToKit={handleAddToKit}
               cancel={() => setIsAddToKitModalOpen(false)}
               kitOptions={itemKitOptions}
               addToKitQty={addToKitQty}
               setAddToKitQty={setAddToKitQty}
               itemForm={itemForm}
               selectedWarehouse={selectedWarehouseForCheckout}
               warehouseOptions={warehouseOptions}
               handleWarehouseChange={handleWarehouseChange}
               disabledWarehouseSelection={warehouseSelectionDisabled}
               selectedItemNotificationId={selectedItemNotificationId}
            />
         )}
         {isRemoveFromKitModal && (
            <RemoveFromKitModal
               isModalOpen={isRemoveFromKitModal}
               handleClose={() => setIsRemoveFromKitModal(false)}
               handleRemoveFromKit={handleRemoveFromKit}
               itemForm={itemForm}
            />
         )}

         {isQuantityModalOpen && (
            <ItemQuantityModal
               isModalOpen={isQuantityModalOpen}
               handleClose={handleWorkflowModalsClose}
               handleAdjustTotal={handleIncrementDecrementQuantity}
               handleCustomTotalChange={handleCustomTotalChange}
               startWorkflowAction={handleCommenceWorkflow}
               items={selectedItemsForWorkflow}
               checkoutAction={checkoutAction}
               warehouseOptions={warehouseOptions}
               selectedWarehouse={selectedWarehouseForCheckout}
               handleWarehouseChange={handleWarehouseChange}
               handleTransferToWarehouseChange={handleTransferToWarehouseChange}
               selectedTransferToWarehouse={selectedTransferToWarehouse}
               enableLocationTracking={enableLocationTracking}
               warehouseSelectionDisabled={warehouseSelectionDisabled}
            />
         )}
         {isCheckoutModalOpen && (
            <CheckoutModal
               isModalOpen={isCheckoutModalOpen}
               handleClose={handleWorkflowModalsClose}
               checkoutAction={checkoutAction}
               selectedItems={selectedItemsForWorkflow}
               handlePageCancelAndReset={closeModalAndRefresh}
               selectedWarehouse={selectedWarehouseForCheckout}
               quantityInCart={
                  selectedItemsForWorkflow[0]?.quantityForCart ?? 1
               }
               selectedTransferToWarehouse={selectedTransferToWarehouse}
               viewTitle
               isAdminCompanyUser={!isEmployeeUser}
            />
         )}

         {isAdjustModalOpen && (
            <AdjustModal
               isOpen={isAdjustModalOpen}
               setIsOpen={setIsAdjustModalOpen}
               ItemId={itemId}
               enableLocationTracking={enableLocationTracking}
               onAdjustComplete={closeModalAndRefresh}
               handleSnackbar={handleSnackbar}
               selectedWarehouse={selectedWarehouseForCheckout}
               handleWarehouseChange={handleWarehouseChange}
               itemWarehouses={itemForm?.Warehouses ?? []}
               warehouseOptions={warehouseOptions}
               disabledWarehouseSelection={warehouseSelectionDisabled}
               isQuantityItem={itemForm.ToolType == ToolTypes['Quantity Item']}
               locationOptions={locationOptions}
               connectionOptions={connectionOptions}
            />
         )}
         {isWarningModalOpen && (
            <WarningModal
               isModalOpen={isWarningModalOpen}
               handleClose={handleCancelDismissAll}
               title="Dismiss All"
               message="Are you sure you want to dismiss all info tasks? This action cannot be undone."
               primaryButtonTitle="Confirm"
               primaryButtonMethod={handleConfirmDismissAll}
               primaryButtonVariant="danger"
               secondaryButtonTitle="Cancel"
               secondaryButtonMethod={handleCancelDismissAll}
               secondaryButtonVariant="tertiary"
            />
         )}
         <SnackBar
            open={
               snackbar.message !== '' && snackbar.severity !== Severity.INFO
            }
            message={snackbar.message}
            severity={snackbar.severity}
            resetMessageStateHandler={() => setSnackbar(closedSnackbarState)}
         />
      </section>
   )
}

export default TasksList
